import React, { useState, useEffect, useRef } from "react";
import "./CommentsBlock.css";
import dotsIcon from "../../assets/dots.svg";
import Avatar from "../Avatar/Avatar";
import { useTranslation } from "react-i18next";
import CommentContextMenu from "./CommentContextMenu";
import CommentAttachments from "./CommentAttachments";
import locationIcon from "../../assets/location.svg";
import drawingIcon from "../../assets/drawing.svg";
import momentTz from "moment-timezone";
import { CountdownCircleTimer } from "react-countdown-circle-timer";
import emojiIcon from "../../assets/emojiLogo.svg";
import TagComponent from "../TagsSuggestionDropdown/TagComponent";
import UserPopup from "./UserPopup";
import Tag from "../TagsSuggestionDropdown/Tag.jsx";
import momentWithLocales from "moment/min/moment-with-locales";
import { ReactComponent as CheckIcon } from "../../assets/blackCheck.svg";
import { createTag, createVersionComment } from "../../store/projectsSlice";
import { fetchCommentReplies } from "../../store/appSlice";
import {
  currentOrganizationSelector,
  userSelector,
} from "../../store/selectors";
import { useDispatch, useSelector } from "react-redux";
import EmojiContextMenu from "./EmojiContextMenu";
import toast from "react-hot-toast";
import {
  getOrganizationReactions,
  removeCommentReaction,
  setCommentReaction,
} from "../../store/usersSlice";
import { update } from "lodash";

const colorsArray = [
  "#9D7BFF",
  "#EA5B6F",
  "#FF78B5",
  "#C64CBF",
  "#B69BF0",
  "#F49BAB",
  "#D23D9B",
  "#CA6DC1",
  "#6B97FD",
  "#5070FF",
  "#3386FF",
  "#FFBA5F",
  "#E98D35",
  "#FD895F",
  "#FAFA92",
  "#FEFFC1",
  "#FFCD67",
  "#AFC55E",
  "#3EDD95",
  "#88D669",
  "#87DDFF",
  "#2BC1DF",
  "#17B7AC",
  "#93DA97",
];

const popElement = (el) => {
  if (!el || !el.classList) return;
  el.classList.remove("reaction-pop");
  void el.offsetWidth; // форсируем reflow, чтобы анимация перезапускалась
  el.classList.add("reaction-pop");

  const handleEnd = (event) => {
    // animationend всплывает от детей — реагируем только на сам элемент
    if (event.target !== el) return;
    el.classList.remove("reaction-pop");
    el.removeEventListener("animationend", handleEnd);
  };
  el.addEventListener("animationend", handleEnd);
};

const CommentsBlock = ({
  commentsArray = [],
  sharedOrgId,
  totalTime,
  myProjectRole,
  isShared,
  isViewer,
  onArchive,
  onRestore,
  replies,
  setReplies,
  updateComments,
  onRemoveCommentFromMarked,
  isSendingComment,
  videoControls,
  videoRef,
  currentTime,
  onPressTimestamp,
  onDelete,
  onForceDelete,
  commentsFilter,
  versionId,
  highlightCommentId, // NEW PROP
  tagsDefaultColors,
  selectedTag,
  setSelectedTag,
  onTagColorChange,
  onTagColorConfigure,
  onSetEditedComment,
  setCurrentHoveredCommentId,
  currentHoveredCommentId,
  onShowSharedNeedLogin,
  newMarkerInfo,
  onSetCommentsFilter,
  setNewMarkerInfo,
  tagsArray,
  onRetryPendingComment,
  onDeletePendingComment,
  onEditPendingComment,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const currentOrganization = useSelector(currentOrganizationSelector);
  const [comments, setComments] = useState(commentsArray);
  const user = useSelector(userSelector);
  const [activeMenuId, setActiveMenuId] = useState(null);
  const [openReplyId, setOpenReplyId] = useState(null);
  const [isDefaultLikeHovered, setIsDefaultLikeHovered] = useState(false);
  const [replyInputs, setReplyInputs] = useState({});
  const [hoveredUser, setHoveredUser] = useState(null);
  const [popupPosition, setPopupPosition] = useState({ top: 0, left: 0 });
  const [currentEditedComment, setCurrentEditedComment] = useState(false);
  const [tagsCounts, setTagsCounts] = useState({});
  const [reactionsArray, setReactionsArray] = useState([]);
  const [commentsEmojies, setCommentsEmojies] = useState({});
  const [shouldNotHighlightComment, setShouldNotHighlightComment] =
    useState(false);
  const [contextMenuPosition, setContextMenuPosition] = useState({
    top: 0,
    left: 0,
  });
  const [isEmojiContextMenuVisible, setIsEmojiContextMenuVisible] =
    useState(false);
  // For scroll and highlight
  const commentRefs = useRef({});
  const prevCommentsRef = useRef([]);
  const pendingReactionAnimRef = useRef(null);
  const [highlightedCommentId, setHighlightedCommentId] = useState(null);

  // Scroll and highlight on external prop change as well
  useEffect(() => {
    if (highlightCommentId && commentRefs.current[highlightCommentId]) {
      commentRefs.current[highlightCommentId].scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      setHighlightedCommentId(highlightCommentId);
      //setTimeout(() => setHighlightedCommentId(null), 2000);
    }
  }, [highlightCommentId]);

  useEffect(() => {
    setComments(commentsArray);
    let newReactionsComments = {};
    for (let i = 0; i < commentsArray.length; i++) {
      let commentReactions = commentsArray[i].reactions;
      const likesReactionsIndex = commentReactions.findIndex(
        (commentReaction) => {
          return commentReaction.reaction.id === 2;
        }
      );
      const otherReactedByMe = commentReactions.findIndex((commentReaction) => {
        return (
          commentReaction.reaction.id !== 2 &&
          commentReaction.reactedByCurrentUser
        );
      });
      if (likesReactionsIndex < 0 && otherReactedByMe < 0) {
        commentReactions = [
          {
            count: 0,
            reactedByCurrentUser: false,
            reaction: {
              code: "like",
              emoji: "👍",
              id: 2,
              name: "Like",
              orgId: null,
            },
          },
          ...commentReactions,
        ];
      }
      newReactionsComments = {
        ...newReactionsComments,
        [commentsArray[i].id]: commentReactions,
      };
    }
    setCommentsEmojies(newReactionsComments);
  }, [commentsArray]);

  useEffect(() => {
    const pending = pendingReactionAnimRef.current;
    if (!pending) return;
    pendingReactionAnimRef.current = null;
    const el = document.querySelector(
      `[data-comment-id="${pending.commentId}"][data-reaction-id="${pending.reactionId}"]`
    );
    popElement(el);
  }, [commentsEmojies]);

  // Scroll and highlight to the new comment when added
  useEffect(() => {
    const prev = prevCommentsRef.current;
    if (comments && prev) {
      // Find added comment
      const prevIds = prev.map((c) => c.id);
      const added = comments.find((c) => !prevIds.includes(c.id));
      if (added && commentRefs.current[added.id]) {
        // Scroll to and highlight
        commentRefs.current[added.id].scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
        setHighlightedCommentId(added.id);
        // Remove highlight after 2s
        setTimeout(() => setHighlightedCommentId(null), 2000);
      }
    }
    prevCommentsRef.current = comments;

    let tagsNumbers = {};

    for (let i = 0; i < comments.length; i++) {
      if (comments[i].tags) {
        for (let k = 0; k < comments[i].tags.length; k++) {
          if (!tagsNumbers[comments[i].tags[k].id]) {
            tagsNumbers[comments[i].tags[k].id] = 1;
          } else {
            tagsNumbers[comments[i].tags[k].id] += 1;
          }
        }
      }
    }
    setTagsCounts(tagsNumbers);

    for (let i = 0; i < comments.length; i++) {}
  }, [comments]);

  const serverCommentIdsKey = comments
    .filter((comment) => !comment.status)
    .map((comment) => comment.id)
    .join(",");

  useEffect(() => {
    updateReplies();
  }, [serverCommentIdsKey]);

  useEffect(() => {
    if (isShared || !user) return;
    const userProjectRoles = user?.projectRoles || [];
    const currentRole = userProjectRoles.find((role) => {
      return role.allowedContentVersionIds.includes(+versionId);
    });
    const neededOrgId = currentRole?.organizationId || currentOrganization.id;
    dispatch(getOrganizationReactions({ organizationId: neededOrgId })).then(
      (res) => {
        if (!res.error) {
          setReactionsArray(res.payload);
        }
      }
    );
  }, [user]);

  const updateReplies = async () => {
    let newReplies = {};
    for (let i = 0; i < comments.length; i++) {
      const comment = comments[i];

      if (comment.status) {
        newReplies[comment.id] = [];
        continue;
      }

      const result = await dispatch(fetchCommentReplies(comment.id));
      if (!result.error) {
        newReplies[comment.id] = result.payload;
      }
    }
    setReplies(newReplies);
  };

  const handleDelete = async (commentId) => {
    const res = await onDelete(commentId);
    //updateReplies();
    //setComments(comments.filter((comment) => comment.id !== commentId));
  };

  const handleForceDelete = async (commentId) => {
    const res = await onForceDelete(commentId);
    //updateReplies();
    //setComments(comments.filter((comment) => comment.id !== commentId));
  };

  const handleMenuClick = (e, commentId) => {
    e.stopPropagation();

    const rect = e.currentTarget.getBoundingClientRect();
    setContextMenuPosition({
      top: rect.bottom + window.scrollY,
      left: rect.right + window.scrollX - 140,
    });
    setActiveMenuId(activeMenuId === commentId ? null : commentId);
  };

  const handleEmojiMenuClick = (e, commentId) => {
    if (isShared) return;
    // if (isEmojiContextMenuVisible) {
    //   e.stopPropagation();
    //   e.preventDefault();
    //   setIsEmojiContextMenuVisible(false);
    //   return;
    // }
    e.stopPropagation();

    if (isEmojiContextMenuVisible !== commentId) {
      const rect = e.currentTarget.getBoundingClientRect();
      setContextMenuPosition({
        top: rect.bottom + window.scrollY + 10,
        left: rect.right + window.scrollX - 30,
      });
    }

    setIsEmojiContextMenuVisible(
      isEmojiContextMenuVisible === commentId ? null : commentId
    );
  };

  const onAddEmoji = (commentId, emoji) => {
    if (commentId != null && emoji && emoji.id != null) {
      pendingReactionAnimRef.current = {
        commentId,
        reactionId: emoji.id,
      };
    }
    updateComments();
  };
  const handleSetReaction = (commentId, emoji) => {
    if (isShared) return;
    dispatch(
      setCommentReaction({
        commentId: commentId,
        reactionId: emoji.id,
      })
    ).then((res) => {
      if (!res.error) {
        onAddEmoji();
      }
    });
  };

  const handleRemoveReaction = (commentId) => {
    if (isShared) return;
    dispatch(
      removeCommentReaction({
        commentId: commentId,
      })
    ).then((res) => {
      if (!res.error) {
        onAddEmoji();
      }
    });
  };

  const checkTagsBeforeSubmitting = async (commentText) => {
    const tagRegex = /#([\wa-zA-Zа-яА-Яё]+)/g;
    const tags = [];
    let editableComment = commentText;
    let newSelectedTags = [];
    let match;
    while ((match = tagRegex.exec(editableComment)) !== null) {
      editableComment = editableComment.split(match[0]).join("");
      tags.push(match[1]);
      match = tagRegex.exec(editableComment);
    }

    for (let i = 0; i < tags.length; i++) {
      const tagName = tags[i];
      const existingTag = tagsArray.find(
        (tagItem) => tagItem.name.toLowerCase() === tagName.toLowerCase()
      );
      if (!!existingTag) {
        newSelectedTags = [...newSelectedTags, existingTag];
      } else {
        const randomColor =
          colorsArray[Math.floor(Math.random() * colorsArray.length)];
        const tagInfo = {
          name: tagName.toLowerCase(),
          color: randomColor,
          organizationId: sharedOrgId || currentOrganization.id,
        };
        const res = await dispatch(createTag(tagInfo));
        newSelectedTags = [...newSelectedTags, res.payload];
      }
    }

    // Remove all tags from the comment
    let cleanedComment = commentText;
    tags.forEach((tagName) => {
      const tagPattern = new RegExp(`#${tagName}\\s*`, "g");
      cleanedComment = cleanedComment.replace(tagPattern, "");
    });

    // Clean up any extra spaces that might be left
    cleanedComment = cleanedComment.replace(/\s+/g, " ").trim();

    return { cleanedComment, newSelectedTags };
  };

  const handleAddReply = async (commentId) => {
    if (!replyInputs[commentId] || !replyInputs[commentId].trim()) return;
    let commentText = replyInputs[commentId].trim();
    let selectedTags = [];

    if (commentText.indexOf("#") > -1) {
      const res = await checkTagsBeforeSubmitting(commentText);
      commentText = res.cleanedComment;
      selectedTags = res.newSelectedTags;
    }

    let commentData = {
      parentId: commentId,
      content: commentText,
      authorId: user.userId,
    };

    commentData.tagIds = selectedTags.map((tag) => tag.id);

    dispatch(createVersionComment({ versionId, commentData })).then((res) => {
      if (!res.error) {
        const updatedReplies = { ...replies };

        if (!updatedReplies[commentId]) {
          updatedReplies[commentId] = [];
        }

        updatedReplies[commentId].push(res.payload);
        setReplies(updatedReplies);
        setReplyInputs({
          ...replyInputs,
          [commentId]: "",
        });
      }
    });

    // setReplies((prev) => ({
    //   ...prev,
    //   [commentId]: [
    //     ...(prev[commentId] || []),
    //     {
    //       id: Date.now(),
    //       author: "Вы",
    //       time: new Date().toLocaleTimeString("ru-RU", {
    //         hour: "2-digit",
    //         minute: "2-digit",
    //       }),
    //       content: replyInput.trim(),
    //     },
    //   ],
    // }));
    // setReplyInput("");
  };
  const handleTimestampClick = (comment) => {
    let timecode = "";
    if (comment.timecode.includes("-")) {
      const [timecodeStart, timecodeEnd] = comment.timecode.split(" - ");
      videoControls.seek(+timecodeStart);
      timecode = +timecodeStart;
    } else {
      videoControls.seek(+comment.timecode);
      timecode = +comment.timecode;
    }

    const newPosition = Math.min((timecode / totalTime) * 100, 100);

    const updatedNewMarkerInfo = {
      ...newMarkerInfo,
      timecode,
      position: newPosition,
      timecodeEnd: null,
      positionEnd: null,
    };

    setNewMarkerInfo(updatedNewMarkerInfo);

    // Показать точку/рисунок этого комментария поверх кадра (или снять чужие)
    const hasAnnotation =
      !!comment.markerPoint ||
      !!(
        comment.drawing &&
        comment.drawing.strokes &&
        comment.drawing.strokes.length
      );
    onPressTimestamp(
      hasAnnotation
        ? {
            markerPoint: comment.markerPoint || null,
            drawing: comment.drawing || null,
          }
        : null,
      timecode
    );
  };

  const handleUserAvatarMouseEnter = (e, user) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setPopupPosition({
      top: rect.top - 10,
      left: rect.left - 230,
    });
    setHoveredUser(user);
  };

  const handleUserAvatarMouseLeave = () => {
    setHoveredUser(null);
  };

  const renderCommentContent = (comment, isReply = false) => {
    const content = comment.content;
    // Split content by @ mentions
    const parts = content.split(/(@[^\s]+)/g);
    const commentText = parts.map((part, index) => {
      if (part.startsWith("@")) {
        return (
          <span key={index} className="user-mention">
            {part}
          </span>
        );
      }
      return part;
    });

    return (
      <div style={{ display: "inline-block" }}>
        {comment.timecode && !isReply && (
          <>
            <span className="comment-timecode-inline">
              {formatTimecode(comment.timecode)}
            </span>
            {"  "}
          </>
        )}

        <span className="comment-text">{commentText}</span>
      </div>
    );
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const formattedDate = momentTz(dateString).tz("etc/gmt-6");
    const today = momentTz();
    let res = "";
    if (!momentTz(dateString).isSame(today, "day")) {
      res = formattedDate.format("DD.MM");
      const fromNow = momentWithLocales(dateString).locale("ru").fromNow();
      res = fromNow;
    } else {
      res = formattedDate.format("HH:mm");
    }
    return res;
  };

  const formatTimecode = (timecode) => {
    if (!timecode.includes("-")) {
      let numberOfMinutes = Math.floor(+timecode / 60);
      let numberOfSeconds = Math.floor(+timecode - 60 * numberOfMinutes);

      if (numberOfMinutes < 10)
        numberOfMinutes = "0" + numberOfMinutes.toString();
      if (numberOfSeconds < 10)
        numberOfSeconds = "0" + numberOfSeconds.toString();
      return `${numberOfMinutes}:${numberOfSeconds}`;
    }

    const [timeRangeStart, timeRangeEnd] = timecode.split(" - ");

    let numberOfMinutesStart = Math.floor(+timeRangeStart / 60);
    let numberOfSecondsStart = Math.floor(
      +timeRangeStart - 60 * numberOfMinutesStart
    );
    if (numberOfMinutesStart < 10)
      numberOfMinutesStart = "0" + numberOfMinutesStart.toString();
    if (numberOfSecondsStart < 10)
      numberOfSecondsStart = "0" + numberOfSecondsStart.toString();

    let numberOfMinutesEnd = Math.floor(+timeRangeEnd / 60);
    let numberOfSecondsEnd = Math.floor(
      +timeRangeEnd - 60 * numberOfMinutesEnd
    );
    if (numberOfMinutesEnd < 10)
      numberOfMinutesEnd = "0" + numberOfMinutesEnd.toString();
    if (numberOfSecondsEnd < 10)
      numberOfSecondsEnd = "0" + numberOfSecondsEnd.toString();

    return `${numberOfMinutesStart}:${numberOfSecondsStart} - ${numberOfMinutesEnd}:${numberOfSecondsEnd}`;
  };

  const handleEditComment = (comment) => {
    onSetEditedComment(comment);
  };

  return (
    <div className="comments-block">
      {comments.map((comment) => (
        <div
          key={comment.id}
          ref={(el) => {
            commentRefs.current[comment.id] = el;
          }}
          className={`comment-item ${
            commentsFilter === "archived" ? "archived" : ""
          }${
            highlightedCommentId === comment.id && !shouldNotHighlightComment
              ? " highlight"
              : ""
          } ${
            currentHoveredCommentId === comment.id && !shouldNotHighlightComment
              ? " hovered-highlight"
              : ""
          }${comment.status ? ` comment-item--${comment.status}` : ""}`}
        >
          <div
            className="comment-fields"
            onClick={() => {
              handleTimestampClick(comment);
            }}
            onMouseEnter={() => setCurrentHoveredCommentId(comment.id)}
            onMouseLeave={() => setCurrentHoveredCommentId(null)}
          >
            <div className="comment-header">
              <div className="comment-author-info">
                <Avatar
                  user={comment.author}
                  name={comment.author.email}
                  size={24}
                />
                <span className="comment-author">
                  {(comment.author.firstName &&
                    `${comment.author.firstName} ${comment.author.lastName}`) ||
                    comment.author.email}
                </span>
              </div>
              <span className="comment-time">
                {formatDate(comment.createdDate)}
              </span>
              {comment.status === "sending" && (
                <span className="comment-status-label">
                  {t("comments.pending.sending")}
                </span>
              )}
              {comment.status === "failed" && (
                <span className="comment-status-label comment-status-label--failed">
                  {t("comments.pending.failed")}
                </span>
              )}
              {comment.status === "editing" && (
                <span className="comment-status-label comment-status-label--editing">
                  {t("comments.pending.editing")}
                </span>
              )}
            </div>

            <div className="comment-content-wrapper">
              <div
                className="comment-tags"
                style={
                  (comment.tags && comment.tags.length && { marginTop: 5 }) ||
                  {}
                }
              >
                {(comment.tags &&
                  comment.tags.length &&
                  comment.tags.map((tag) => (
                    // <Tag
                    //   tag={tag}
                    //   tagsDefaultColors={tagsDefaultColors}
                    //   key={tag.id}
                    //   onClick={(e) => {
                    //     e.stopPropagation();
                    //     e.preventDefault();
                    //     if (!tag.color) tag.color = tagsDefaultColors[tag.id];
                    //     setSelectedTag(
                    //       selectedTag.name === "Все"
                    //         ? tag
                    //         : { name: "Все", id: -1 }
                    //     );
                    //   }}
                    // />
                    <TagComponent
                      tagsDefaultColors={tagsDefaultColors}
                      key={tag.id}
                      tag={tag}
                      needCount={true}
                      tagsCounts={tagsCounts}
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        if (!tag.color) tag.color = tagsDefaultColors[tag.id];
                        setSelectedTag(
                          selectedTag.name === "Все"
                            ? tag
                            : { name: "Все", id: -1 }
                        );
                      }}
                      onTagColorChange={onTagColorChange}
                      onTagColorConfigure={onTagColorConfigure}
                    />
                  ))) ||
                  null}
                {(comment.mentions && comment.mentions.length && (
                  <div>
                    <Avatar
                      user={comment.mentions[0]}
                      name={comment.mentions[0].email}
                      size={27}
                      onMouseEnter={(e) =>
                        handleUserAvatarMouseEnter(e, comment.mentions[0])
                      }
                      onMouseLeave={handleUserAvatarMouseLeave}
                    />
                  </div>
                )) ||
                  null}
                {comment.statuses &&
                  comment.statuses.map((status) => (
                    <div
                      key={status.id}
                      className="comment-status"
                      style={{ backgroundColor: status.color }}
                    >
                      <span className="comment-status-text">{status.id}</span>
                    </div>
                  ))}
              </div>

              <span className="comment-text">
                {renderCommentContent(comment)}
              </span>
            </div>

            {(comment.markerPoint ||
              (comment.drawing && comment.drawing.strokes?.length)) && (
              <div className="comment-annotation-badges">
                {comment.markerPoint && (
                  <span
                    className="comment-annotation-badge"
                    title={t("comments.point.badge")}
                  >
                    <img src={locationIcon} alt="" />
                    {t("comments.point.badge")}
                  </span>
                )}
                {comment.drawing && comment.drawing.strokes?.length ? (
                  <span
                    className="comment-annotation-badge"
                    title={t("comments.drawing.badge")}
                  >
                    <img src={drawingIcon} alt="" />
                    {t("comments.drawing.badge")}
                  </span>
                ) : null}
              </div>
            )}
            <CommentAttachments attachments={comment.attachments} />
            <div className="comment-emoji-row">
              {commentsEmojies[comment.id] &&
                commentsEmojies[comment.id].map((commentReaction, index) => (
                  <div
                    className="comment-emoji-item"
                    data-comment-id={comment.id}
                    data-reaction-id={commentReaction.reaction.id}
                    style={{
                      backgroundColor: commentReaction.reactedByCurrentUser
                        ? "#191d248a"
                        : commentReaction.count === 0
                          ? "transparent"
                          : "#333b4b8a",
                      marginLeft:
                        index === 0
                          ? commentReaction.reactedByCurrentUser
                            ? -12
                            : -13
                          : 0,
                      opacity:
                        index === 0
                          ? !commentReaction.reactedByCurrentUser
                            ? !(isDefaultLikeHovered === comment.id)
                              ? 0.5
                              : 1
                            : 1
                          : 1,
                      paddingRight:
                        index === 0
                          ? commentReaction.reactedByCurrentUser
                            ? 10
                            : 5
                          : 10,
                    }}
                    onClick={(e) => {
                      // if (isViewer) return;
                      e.preventDefault();
                      e.stopPropagation();
                      if (isShared) {
                        onShowSharedNeedLogin();
                        return;
                      }
                      popElement(e.currentTarget);
                      if (commentReaction.reactedByCurrentUser) {
                        handleRemoveReaction(comment.id);
                      }
                      if (!commentReaction.reactedByCurrentUser) {
                        handleSetReaction(comment.id, commentReaction.reaction);
                      }
                    }}
                    onMouseEnter={() => {
                      if (index === 0) {
                        setIsDefaultLikeHovered(comment.id);
                      }
                      setShouldNotHighlightComment(true);
                    }}
                    onMouseLeave={() => {
                      if (index === 0) {
                        setIsDefaultLikeHovered(null);
                      }
                      setShouldNotHighlightComment(false);
                    }}
                  >
                    <span
                      style={{ fontSize: 22, userSelect: "none" }}
                    >{`${commentReaction.reaction.emoji}`}</span>
                    {!!commentReaction.count && (
                      <span className="comment-emoji-item-count">
                        {commentReaction.count}
                      </span>
                    )}
                  </div>
                ))}
              <div
                className="comment-emoji-item-transparent"
                onClick={(e) => {
                  if (isShared) {
                    onShowSharedNeedLogin();
                    return;
                  }
                  handleEmojiMenuClick(e, comment.id);
                }}
                onMouseEnter={() => setShouldNotHighlightComment(true)}
                onMouseLeave={() => setShouldNotHighlightComment(false)}
                style={{
                  width: "auto",
                  paddingLeft: 2,
                  paddingRight: 2,
                }}
              >
                <img
                  id="emoji-icon"
                  src={emojiIcon}
                  style={{ width: 16, height: 16 }}
                />
                <EmojiContextMenu
                  emojiArray={reactionsArray}
                  isLikeVisible={
                    commentsEmojies &&
                    commentsEmojies[comment.id] &&
                    commentsEmojies[comment.id].length &&
                    commentsEmojies[comment.id][0].reaction.id !== 2
                  }
                  onAddEmoji={onAddEmoji}
                  isOpen={isEmojiContextMenuVisible === comment.id}
                  onClose={(e) => handleEmojiMenuClick(e, comment.id)}
                  comment={comment}
                  contextMenuPosition={contextMenuPosition}
                />
              </div>
            </div>
            {(!isShared && !isViewer && (
              <div className="comment-footer">
                {!isShared && (
                  <div>
                    <span
                      className="reply-text"
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        if (isViewer) return;
                        if (isShared) {
                          onShowSharedNeedLogin();
                          return;
                        }
                        setOpenReplyId(
                          openReplyId === comment.id ? null : comment.id
                        );
                      }}
                    >
                      {`${t("comments.reply")}`}
                    </span>
                    <span className="reply-text-count">
                      {replies[comment.id] && replies[comment.id].length
                        ? ` ${replies[comment.id].length}`
                        : ""}
                    </span>
                  </div>
                )}
                <div
                  style={{
                    position: "relative",
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    gap: 10,
                  }}
                >
                  {!comment.status &&
                    commentsFilter !== "archived" &&
                    !isShared &&
                    (!myProjectRole ||
                      myProjectRole.role !== "COMMENTER" ||
                      comment.author.id === user.userId) && (
                      <div
                        className="comment-solved-button"
                        onMouseEnter={() => setShouldNotHighlightComment(true)}
                        onMouseLeave={() => setShouldNotHighlightComment(false)}
                        onClick={() => {
                          onArchive(comment.id);
                          toast.success("Комментарий помечен как решенный");
                        }}
                      >
                        <CheckIcon
                          style={{
                            width: 24,
                            height: 24,
                            color: "rgb(121, 201, 121)",
                            transform: "scale(1)",
                          }}
                        />
                      </div>
                    )}
                  {comment.status === "sending" && (
                    <span className="comment-status-actions comment-status-actions--sending">
                      {t("comments.pending.sendingShort")}
                    </span>
                  )}

                  {comment.status === "failed" && (
                    <span className="comment-status-actions">
                      <button
                        className="comment-status-btn"
                        onClick={() => onRetryPendingComment(comment.id)}
                      >
                        {t("comments.pending.retry")}
                      </button>
                      <button
                        className="comment-status-btn"
                        onClick={() => onEditPendingComment(comment)}
                      >
                        {t("comments.pending.edit")}
                      </button>
                      <button
                        className="comment-status-btn comment-status-btn--danger"
                        onClick={() => onDeletePendingComment(comment.id)}
                      >
                        {t("comments.pending.delete")}
                      </button>
                    </span>
                  )}
                  {(!comment.status &&
                    !isShared &&
                    (!myProjectRole ||
                      myProjectRole.role !== "COMMENTER" ||
                      comment.author.id === user.userId) && (
                      <>
                        <img
                          src={dotsIcon}
                          alt="menu"
                          className="menu-icon"
                          onClick={(e) => handleMenuClick(e, comment.id)}
                        />
                        <CommentContextMenu
                          comment={comment}
                          commentsFilter={commentsFilter}
                          isOpen={activeMenuId === comment.id}
                          onClose={() => setActiveMenuId(null)}
                          onArchive={() => onArchive(comment.id)}
                          onRestore={() => onRestore(comment.id)}
                          onDelete={() => handleDelete(comment.id)}
                          onForceDelete={() => handleForceDelete(comment.id)}
                          isArchived={commentsFilter === "archived"}
                          position={{ top: "100%", right: 0 }}
                          onEdit={() => handleEditComment(comment)}
                          contextMenuPosition={contextMenuPosition}
                        />
                      </>
                    )) || <div style={{ width: 5, height: 5 }} />}
                </div>
              </div>
            )) ||
              null}
          </div>
          <div className="replies-block">
            {(replies && replies[comment.id] && replies[comment.id].length && (
              <div className="replies-list">
                {(replies[comment.id] || []).map((reply) => (
                  <div className="reply-item" key={reply.id}>
                    <div className="reply-meta">
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "row",
                          alignItems: "center",
                        }}
                      >
                        <Avatar
                          user={reply.author}
                          size={24}
                          name={reply.author.email}
                        />
                        <span className="reply-author">
                          {reply.author.firstName
                            ? `${reply.author.firstName} ${reply.author.lastName}`
                            : reply.author.nickname || reply.author.email}
                        </span>
                      </div>
                      <span className="reply-time">
                        {formatDate(reply.createdDate)}
                      </span>
                    </div>

                    <div style={{ display: "flex", gap: 8, marginTop: 5 }}>
                      {(reply.tags &&
                        reply.tags.length &&
                        reply.tags.map((tag) => (
                          <TagComponent
                            tagsDefaultColors={tagsDefaultColors}
                            key={tag.id}
                            tag={tag}
                            onClick={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                              if (!tag.color)
                                tag.color = tagsDefaultColors[tag.id];
                              setSelectedTag(
                                selectedTag.name === "Все"
                                  ? tag
                                  : { name: "Все", id: -1 }
                              );
                            }}
                            onTagColorChange={onTagColorChange}
                            onTagColorConfigure={onTagColorConfigure}
                          />
                        ))) ||
                        null}
                    </div>
                    <div className="reply-content">
                      <span className="reply-text">
                        {renderCommentContent(reply, true)}
                      </span>
                      <div style={{ position: "relative" }}>
                        {(!isShared &&
                          !isViewer &&
                          (!myProjectRole ||
                            myProjectRole.role !== "COMMENTER" ||
                            reply.author.id === user.userId) && (
                            <>
                              <img
                                src={dotsIcon}
                                alt="menu"
                                className="menu-icon"
                                onClick={(e) => handleMenuClick(e, reply.id)}
                              />
                              <CommentContextMenu
                                isReply={true}
                                comment={reply}
                                commentsFilter={commentsFilter}
                                isOpen={activeMenuId === reply.id}
                                onClose={() => setActiveMenuId(null)}
                                onArchive={() => onArchive(reply.id)}
                                onRestore={() => onRestore(reply.id)}
                                onDelete={() => handleDelete(reply.id)}
                                onForceDelete={() =>
                                  handleForceDelete(reply.id)
                                }
                                isArchived={commentsFilter === "archived"}
                                position={{ top: "100%", right: 0 }}
                                onEdit={() => handleEditComment(reply)}
                                contextMenuPosition={contextMenuPosition}
                              />
                            </>
                          )) || <div style={{ width: 5, height: 5 }} />}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )) ||
              null}
            {(openReplyId === comment.id && (
              <div className="reply-input-row">
                <input
                  className="reply-input"
                  value={replyInputs[comment.id] || ""}
                  onChange={(e) =>
                    setReplyInputs({
                      ...replyInputs,
                      [comment.id]: e.target.value,
                    })
                  }
                  autoFocus={true}
                  placeholder="Напишите комментарий"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleAddReply(comment.id);
                  }}
                />
                <button
                  className="reply-send-btn"
                  onClick={() => handleAddReply(comment.id)}
                >
                  <span role="img" aria-label="send">
                    ➤
                  </span>
                </button>
              </div>
            )) ||
              null}
          </div>
          {comment.isMarkedForArchive && commentsFilter !== "archive" && (
            <div
              style={{
                width: "100%",
                left: 0,
                right: 0,
                borderRadius: 16,
                height: "100%",
                padding: 15,
                boxSizing: "border-box",
                position: "absolute",
                backgroundColor: "#312940db",
                color: "white",
                fontWeight: 500,
                display: "flex",
                flexDirection: "column",
                fontFamily: "NT Somic",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  width: "100%",
                  fontSize: 14,
                }}
              >
                <span>Комментарий помечен как</span>
                <div
                  onClick={() => onSetCommentsFilter("archived")}
                  style={{
                    // textDecoration: "underline",
                    cursor: "pointer",
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    gap: 4,
                    color: "#9a6aff",
                  }}
                >
                  <CheckIcon
                    style={{
                      width: 16,
                      height: 16,
                      //color: "rgb(121, 201, 121)",
                      color: "#9a6aff",
                      transform: "scale(1)",
                    }}
                  />
                  <span>решённый</span>
                </div>
              </div>
              <CountdownCircleTimer
                isPlaying
                duration={5}
                size={50}
                rotation="counterclockwise"
                strokeWidth={4}
                colors={["#9a6aff"]}
                onComplete={() => onRemoveCommentFromMarked(comment.id)}
                // colorsTime={[7, 5, 2, 0]}
              >
                {({ remainingTime }) => remainingTime}
              </CountdownCircleTimer>
              <div
                onClick={() => {
                  onRestore(comment.id);
                }}
                style={{ color: "#9a6aff", cursor: "pointer" }}
              >
                Отменить
              </div>
            </div>
          )}
        </div>
      ))}
      <UserPopup
        user={hoveredUser}
        isVisible={!!hoveredUser}
        position={popupPosition}
      />
    </div>
  );
};

export default CommentsBlock;
